import { ReactElement } from 'react';
import {
  abbreviateScenarioValue,
  SCENARIO_NEGATIVE_PILL_COLOR
} from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { PercentChange } from '@Components/PercentChange';
import {
  calcPct,
  suppressSubThresholdModelNoise,
  toPrettyPercent
} from '@Pages/GeoMMM/ScenarioPlanning/Modals/ChangeImpact/ChangeImpactUtils';
import { Size, StretchMode } from '@Components/designConstants';

const deltaClassName = (delta: number): string => {
  if (delta > 0) return 'delta-value positive';
  if (delta < 0) return 'delta-value negative';
  return 'delta-value';
};

interface OverviewMetricProps {
  title: string;
  baselineValue: number;
  oldValue: number;
  newValue: number;
  prefix: string;
  isFirstRun?: boolean;
}

export const OverviewMetric = ({
  title,
  baselineValue,
  oldValue,
  newValue,
  prefix,
  isFirstRun = false
}: OverviewMetricProps): ReactElement => {
  const monetary = prefix === '$';
  // Compute deltas/percents from values rounded to the displayed precision so two visually
  // identical labels never produce a non-zero "<1%" pill. Spend/revenue display as whole
  // dollars (decimals=0); ROAS keeps 2 decimals.
  const decimals = monetary ? 0 : 2;
  const factor = 10 ** decimals;
  const roundedBaseline = Math.round(baselineValue * factor) / factor;
  const rounded = {
    oldValue: Math.round(oldValue * factor) / factor,
    newValue: Math.round(newValue * factor) / factor,
    baselineValue: roundedBaseline
  };
  // Dollar gaps under the shared noise floor are no change here too, as in the cards and tables.
  const { oldValue: roundedOld, newValue: roundedNew } = monetary
    ? suppressSubThresholdModelNoise(rounded)
    : rounded;
  const oldPctFromBaseline = calcPct(roundedOld, roundedBaseline);
  const newPctFromBaseline = calcPct(roundedNew, roundedBaseline);
  const delta = roundedNew - roundedOld;
  const deltaPct = newPctFromBaseline - oldPctFromBaseline;
  // toPrettyPercent now signs positives itself, so just pass the value through.
  const signedDeltaPct = toPrettyPercent(deltaPct);
  return (
    <div className='overview-metric'>
      <div className='overview-metric-title'>{title}</div>
      <div className='overview-metric-body'>
        <div className='overview-metric-row'>
          <div className='row-label'>{isFirstRun ? 'Business as Usual' : 'Previous'}</div>
          <div className='row-right'>
            <div className='value-column'>
              <div className='prev-value'>{abbreviateScenarioValue(oldValue, { monetary })}</div>
            </div>
            <div className='pct-column' style={isFirstRun ? { visibility: 'hidden' } : undefined}>
              <PercentChange
                stretchMode={StretchMode.FIT}
                negativeColor={SCENARIO_NEGATIVE_PILL_COLOR}
                percentChange={oldPctFromBaseline}
                size={Size.SM}
              />
            </div>
          </div>
        </div>
        <div className='overview-metric-row'>
          <div className='row-label'>New</div>
          <div className='row-right'>
            <div className='value-column'>
              <div className='new-value'>{abbreviateScenarioValue(newValue, { monetary })}</div>
              <div className={deltaClassName(delta)}>
                {abbreviateScenarioValue(delta, { monetary, showSign: true })}
              </div>
            </div>
            <div className='pct-column'>
              <PercentChange
                stretchMode={StretchMode.FIT}
                negativeColor={SCENARIO_NEGATIVE_PILL_COLOR}
                percentChange={newPctFromBaseline}
                size={Size.SM}
              />
              <div className={deltaClassName(delta)}>{signedDeltaPct}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
